/**
 * LUSTRO mobile/src/utils/recommendationCatalog.ts (kanon) — katalog zaleceń PER LOKAL: pozycje
 * wbudowane, scalanie z API (migr. 059, GET /api/recommendations/apartment-items), rozpoznawanie
 * zapisanych bloków (także po zmianie treści pozycji — `legacyTexts`), budowanie treści z pól
 * wyboru i odtwarzanie pól z treści.
 *
 * ⚠️ Kod poniżej tego nagłówka ma być IDENTYCZNY z kanonem mobilnym — pilnuje tego
 * backend/test/apartmentRecommendationItemsParity.test.js (porównanie bez komentarzy).
 * Zmiany zaczynają się w mobilce; tu wklejasz 1:1.
 */

export type SelectionMode = 'none' | 'optional' | 'required';
export type DevicePosition = 'prefix' | 'suffix';
/** Pozycja z zachowaniem zaszytym w kodzie: „Szczelne Mieszkanie" ma podformularz dopływu. */
export type BuiltinKind = 'airflow';

export interface RecommendationItem {
  /** Stabilne id (seed = dotychczasowe id; nowe nadaje serwer, nigdy nie wraca do użycia). */
  id: string;
  label: string;
  baseText: string;
  /** Poprzednie treści pozycji — tylko do rozpoznawania starych zapisów. */
  legacyTexts: string[];
  devicesMode: SelectionMode;
  devicePosition: DevicePosition;
  /** „Inne (wpisz)" na liście urządzeń. */
  allowOtherDevice: boolean;
  roomsMode: SelectionMode;
  /** Podzbiór słownika pomieszczeń (w tej kolejności); null = cały słownik. */
  roomIds: string[] | null;
  /** „Inne (wpisz)" na liście pomieszczeń. */
  allowOtherRoom: boolean;
  builtinKind: BuiltinKind | null;
  /** false = wycofana: nie ma jej na liście wyboru, ale stary zapis nadal się rozpoznaje. */
  active: boolean;
}

/** Pozycja listy urządzeń (kształt list z migr. 054 — `recommendation_device_options`). */
export interface ChoiceOption {
  id: string;
  label: string;
  protocolText?: string | null;
  active?: boolean;
}

/** Pomieszczenie słownika (id = wartość zapisywana dotąd, label = nazwa do treści). */
export interface RoomChoice {
  id: string;
  label: string;
}

export interface Selection {
  /** id urządzeń w kolejności zaznaczenia */
  devices: string[];
  otherDevice: string;
  /** id pomieszczeń w kolejności zaznaczenia */
  rooms: string[];
  otherRoom: string;
}

export const EMPTY_SELECTION: Selection = { devices: [], otherDevice: '', rooms: [], otherRoom: '' };

export const ROOMS_LINE_PREFIX = 'Pomieszczenia: ';
const DEVICE_PREFIX_SEPARATOR = ' - ';
const LIST_SEPARATOR = ', ';

/**
 * Pozycje wbudowane = seed migracji 059 + poprawki 072 (kolejność = sort_order). Teksty z docx Bronka DOSŁOWNIE
 * (zwinięte tylko podwójne spacje) poza dwiema literówkami poprawionymi w 072 (D14 „powietrzno-spalinowym”,
 * D15 „kontrolę”/„przewodów”, raport uwag Bronka 2026-10-19) — treść sprzed poprawki jest w legacyTexts.
 */
export const DEFAULT_RECOMMENDATION_ITEMS: RecommendationItem[] = [
  {
    id: 'pg_cog',
    label: 'PG, COG - Łącznik wymaga wymiany',
    baseText: 'z otwartą komorą spalania. Łącznik wymaga wymiany. Nowy (kwasoodporny), należy osadzić w kominie na szczelnie dopasowanej rozecie umożliwiając jego wyjmowanie, a tym samym umożliwiając dostęp do wlotu przewodu kominowego w celu kontroli.',
    legacyTexts: ['z otwartą komorą spalania. Łącznik wymaga wymiany. Nowy (kwasoodporny), należy osadzić w kominie na szczelnie dopasowanej rozecie umożliwiającej wyjście i dostęp do wlotu przewodu kominowego.'],
    devicesMode: 'required',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'required',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'lacznik',
    label: 'ŁĄCZNIK – wymiana i doszczelnienie',
    baseText: 'Łącznik urządzenia wymaga wymiany/ doszczelnienia/ z usunięciem nieszczelności wokół podłączenia:',
    legacyTexts: [],
    devicesMode: 'required',
    devicePosition: 'suffix',
    allowOtherDevice: true,
    roomsMode: 'optional',
    roomIds: ['kuchnia', 'lazienka', 'wc', 'pokoj', 'piwnica', 'przedpokoj'],
    allowOtherRoom: true,
    builtinKind: null,
    active: true,
  },
  {
    id: 'kratka',
    label: 'WK, WŁ, Wwc - Kratka wymaga wymiany',
    baseText: 'kratka wymaga wymiany na niezamykaną o przekroju, co najmniej 0,016m2, z uwagi na: brak możliwości zdjęcia, mały przekrój lub żaluzje.',
    legacyTexts: [],
    devicesMode: 'required',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'optional',
    roomIds: ['kuchnia', 'lazienka', 'wc', 'pomieszczenie_kotla_co', 'piwnica', 'pralnia', 'pokoj', 'przedpokoj'],
    allowOtherRoom: true,
    builtinKind: null,
    active: true,
  },
  {
    id: 'brak_dostepu',
    label: 'Brak dostępu do wlotów przewodów',
    baseText: 'brak wymaganego dostępu do wlotów przewodów z uwagi m. in. na: zasłonięcie, zabudowę meblami lub sufitem, mechaniczny wentylator, okap lub prowadzenie kanałami bez stosownej rewizji.',
    legacyTexts: [],
    devicesMode: 'required',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'required',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'wentylator',
    label: 'Mechaniczny wentylator wyciągowy',
    baseText: 'Mechaniczny wentylator wyciągowy zlikwidować',
    legacyTexts: [],
    devicesMode: 'none',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'required',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'sciana_komina',
    label: 'Spękania / nieszczelności ściany komina',
    baseText: 'Usunąć/ doszczelnić lub wymienić okładzinę ścienną nieszczelnego – spękanego komina.',
    legacyTexts: [],
    devicesMode: 'none',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'optional',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'nieszczelnosci',
    label: 'Doszczelnić nieszczelności',
    baseText: 'Doszczelnić /usunąć nieszczelności podłączeń z kominem',
    legacyTexts: [],
    devicesMode: 'none',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'none',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: false,
  },
  {
    id: 'nawiew_kotla',
    label: 'Brak nawiewu przy kotle COW/COG',
    baseText: 'Brak wymaganego stałego nawiewu powietrza zewnętrznego w pomieszczeniu kotła CO z otwartą komorą spalania.',
    legacyTexts: ['Brak stałego nawiewu powietrza zewnętrznego w pomieszczeniu kotła COW lub COG z otwartą komorą spalania.'],
    devicesMode: 'none',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'none',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'doplyw_niewystarczajacy',
    label: 'Szczelne Mieszkanie',
    baseText: 'Dopływ powietrza zewnętrznego do mieszkania jest niewystarczający.',
    legacyTexts: [],
    devicesMode: 'none',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'none',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: 'airflow',
    active: true,
  },
  {
    id: 'nawiew_sanitarny',
    label: 'Brak nawiewu 220 cm²',
    baseText: 'Brak wymaganego nawiewu o pow. 220 cm² w drzwiach pomieszczeń sanitarnych.',
    legacyTexts: [],
    devicesMode: 'none',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'none',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'ocena_techniczna',
    label: 'Urządzenia wymagają oceny',
    baseText: 'Urządzenia grzewcze wymagają oceny technicznej.',
    legacyTexts: [],
    devicesMode: 'optional',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'optional',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'brak_rewizji',
    label: 'Brak Rewizji SPS/WSPS',
    baseText: 'Przewód spalinowy od gazowego kotła CO w układzie powietrzno-spalinowym nie jest wyposażony w wymagany element rewizyjny, służący do oceny i usunięcia ewentualnych zanieczyszczeń, które mogą się gromadzić na odcinku poziomym przewodu spalinowego oraz do ewentualnego badania szczelności gdy zajdzie potrzeba.',
    legacyTexts: [
      'Brak wymaganej rewizji w przewodzie spalinowym od gazowego kotła CO z zamkniętą komorą spalania.',
      'Przewód spalinowy od gazowego kotła CO w układzie powietrznospalinowym nie jest wyposażony w wymagany element rewizyjny, służący do oceny i usunięcia ewentualnych zanieczyszczeń, które mogą się gromadzić na odcinku poziomym przewodu spalinowego oraz do ewentualnego badania szczelności gdy zajdzie potrzeba.',
    ],
    devicesMode: 'none',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'none',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'brak_went',
    label: 'Brak Went.',
    baseText: 'Brak wymaganej wentylacji w pomieszczeniu:',
    legacyTexts: [],
    devicesMode: 'required',
    devicePosition: 'suffix',
    allowOtherDevice: true,
    roomsMode: 'none',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'wspolne_went',
    label: 'Wspólne podłączenie Went.',
    baseText: 'Wspólne podłączenie wentylacji do jednego przewodu kominowego, co jest nieprawidłowe. Podłączenia wymagają odrębnych przewodów grawitacyjnych.',
    legacyTexts: [],
    devicesMode: 'none',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'optional',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
  {
    id: 'cog_went',
    label: 'COG + Wentylacja w jednym przewodzie',
    baseText: 'Przewód spalinowy od gazowego kotła CO i grawitacyjna wentylacja wywiewna pomieszczenia podłączone są do tego samego przewodu, co jest nieprawidłowe – co uniemożliwia kontrolę drożności z czyszczeniem wentylacji, a także uniemożliwia zastosowanie prawidłowego wylotu przewodu ponad dachem – kratka wentylacji grawitacyjnej wymaga indywidualnych przewodów.',
    legacyTexts: ['Przewód spalinowy od gazowego kotła CO i grawitacyjna wentylacja wywiewna pomieszczenia podłączone są do tego samego przewodu, co jest nieprawidłowe – co uniemożliwia kontrole drożności z czyszczeniem wentylacji, a także uniemożliwia zastosowanie prawidłowego wylotu przewodu ponad dachem – kratka wentylacji grawitacyjnej wymaga indywidualnych przewodu.'],
    devicesMode: 'none',
    devicePosition: 'prefix',
    allowOtherDevice: false,
    roomsMode: 'none',
    roomIds: null,
    allowOtherRoom: false,
    builtinKind: null,
    active: true,
  },
];

const MODES: SelectionMode[] = ['none', 'optional', 'required'];

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/**
 * Pozycja z API/cache → RecommendationItem albo null (obrona przed uszkodzonym cache i starszym
 * kształtem). Brakujące pola opcjonalne dostają bezpieczne wartości (tekst bez pól wyboru).
 */
export function normalizeItem(raw: unknown): RecommendationItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isNonEmptyString(r.id) || !isNonEmptyString(r.label) || !isNonEmptyString(r.baseText)) return null;
  const mode = (v: unknown): SelectionMode => (MODES.includes(v as SelectionMode) ? (v as SelectionMode) : 'none');
  const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter(isNonEmptyString) : []);
  return {
    id: r.id,
    label: r.label,
    baseText: r.baseText,
    legacyTexts: strings(r.legacyTexts),
    devicesMode: mode(r.devicesMode),
    devicePosition: r.devicePosition === 'suffix' ? 'suffix' : 'prefix',
    allowOtherDevice: r.allowOtherDevice === true,
    roomsMode: mode(r.roomsMode),
    roomIds: Array.isArray(r.roomIds) ? strings(r.roomIds) : null,
    allowOtherRoom: r.allowOtherRoom === true,
    builtinKind: r.builtinKind === 'airflow' ? 'airflow' : null,
    active: r.active !== false,
  };
}

/**
 * Scala odpowiedź API (`items`) albo jej cache ze stałymi z kodu: pozycja z serwera wygrywa
 * w całości (po id, w kolejności serwera); pozycja wbudowana, której serwer nie zna (starszy
 * backend, uszkodzony/pusty cache), zostaje ze stałych. Lista nigdy nie jest pusta.
 */
export function mergeItems(
  apiItems: unknown,
  defaults: RecommendationItem[] = DEFAULT_RECOMMENDATION_ITEMS,
): RecommendationItem[] {
  const fromApi: RecommendationItem[] = [];
  if (Array.isArray(apiItems)) {
    for (const raw of apiItems) {
      const item = normalizeItem(raw);
      if (item && !fromApi.some((i) => i.id === item.id)) fromApi.push(item);
    }
  }
  return [...fromApi, ...defaults.filter((d) => !fromApi.some((i) => i.id === d.id))];
}

/** Widok szczegółów (urządzenia / pomieszczenia). „Szczelne Mieszkanie" ma własny podformularz —
 *  nigdy widok szczegółów, nawet gdyby katalog z serwera miał inne tryby. */
export const hasDetails = (item: RecommendationItem): boolean =>
  item.builtinKind !== 'airflow' && (item.devicesMode !== 'none' || item.roomsMode !== 'none');

/** Treści, po których rozpoznajemy zapis pozycji: bieżąca + poprzednie. */
const itemTexts = (item: RecommendationItem): string[] =>
  [item.baseText, ...item.legacyTexts].filter((t) => t.length > 0);

/**
 * Od jakiego udziału wspólnych słów (i przy ilu wspólnych słowach) blok uznajemy za edytowaną
 * pozycję katalogu. Progi dobrane tak, by przeszedł typowy dopisek i skrót zdania katalogowego,
 * a zdanie napisane od zera — nie (trafia wtedy do „Inne uwagi", czyli nie ginie).
 */
export const CATALOG_MATCH_THRESHOLD = 0.4;
export const CATALOG_MATCH_MIN_WORDS = 4;

const words = (t: string): string[] =>
  t
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2);

/** Udział słów tekstu katalogowego obecnych w bloku (0, gdy wspólnych słów jest za mało). */
export function overlapWithCatalog(chunk: string, catalogText: string): number {
  const base = words(catalogText);
  if (!base.length) return 0;
  const inChunk = new Set(words(chunk));
  const shared = base.filter((w) => inChunk.has(w)).length;
  if (shared < CATALOG_MATCH_MIN_WORDS) return 0;
  return shared / base.length;
}

export interface ChunkMatch {
  item: RecommendationItem;
  /** Dosłownie znaleziona treść (bieżąca albo poprzednia); null = dopasowanie przybliżone. */
  matchedText: string | null;
}

/**
 * Rozpoznaje blok zapisanego tekstu jako pozycję katalogu (także wycofaną):
 *  1. blok ZAWIERA treść pozycji (bieżącą lub poprzednią) — wygrywa treść stojąca najwcześniej,
 *     przy remisie najdłuższa; obejmuje to prefiks urządzeń, dopisek po treści, wiersz pomieszczeń
 *     i podformularz dopływu (blok z dwiema treściami należy do pierwszej, jak dotąd);
 *  2. inaczej najlepszy udział wspólnych słów ≥ CATALOG_MATCH_THRESHOLD (zdanie edytowane ręcznie).
 */
export function matchChunk(chunk: string, items: RecommendationItem[]): ChunkMatch | undefined {
  let literal: (ChunkMatch & { at: number; length: number }) | undefined;
  for (const item of items) {
    for (const text of itemTexts(item)) {
      const at = chunk.indexOf(text);
      if (at < 0) continue;
      if (!literal || at < literal.at || (at === literal.at && text.length > literal.length)) {
        literal = { item, matchedText: text, at, length: text.length };
      }
    }
  }
  if (literal) return { item: literal.item, matchedText: literal.matchedText };
  let best: { item: RecommendationItem; score: number } | undefined;
  for (const item of items) {
    const score = Math.max(...itemTexts(item).map((t) => overlapWithCatalog(chunk, t)));
    if (score >= CATALOG_MATCH_THRESHOLD && (!best || score > best.score)) best = { item, score };
  }
  return best ? { item: best.item, matchedText: null } : undefined;
}

export const findItemForChunk = (chunk: string, items: RecommendationItem[]): RecommendationItem | undefined =>
  matchChunk(chunk, items)?.item;

/** Pomieszczenia pokazywane przy pozycji (podzbiór słownika w kolejności pozycji). */
export function roomsForItem(item: RecommendationItem, dictionary: RoomChoice[]): RoomChoice[] {
  if (!item.roomIds) return dictionary;
  return item.roomIds
    .map((id) => dictionary.find((r) => r.id === id))
    .filter((r): r is RoomChoice => !!r);
}

/** Skrót urządzenia do treści: `protocolText`, a gdy pusty — nazwa z listy. */
export const choiceText = (o: ChoiceOption): string =>
  (o.protocolText && o.protocolText.trim()) || o.label.trim();

/**
 * Treść bloku z pól wyboru. Urządzenia/pomieszczenia spoza listy (pozycja wycofana w trakcie)
 * wpisują się dosłownie (id). „Inne" tylko tam, gdzie pozycja na nie pozwala.
 */
export function buildText(
  item: RecommendationItem,
  selection: Selection,
  deviceOptions: ChoiceOption[],
  dictionary: RoomChoice[],
): string {
  const devices = item.devicesMode === 'none'
    ? []
    : [
      ...selection.devices.map((id) => {
        const o = deviceOptions.find((x) => x.id === id);
        return o ? choiceText(o) : id;
      }),
      item.allowOtherDevice ? selection.otherDevice.trim() : '',
    ].filter((t) => t.length > 0);
  const rooms = item.roomsMode === 'none'
    ? []
    : [
      ...selection.rooms.map((id) => dictionary.find((r) => r.id === id)?.label ?? id),
      item.allowOtherRoom ? selection.otherRoom.trim() : '',
    ].filter((t) => t.length > 0);
  let head = item.baseText;
  if (devices.length > 0) {
    head = item.devicePosition === 'suffix'
      ? `${item.baseText} ${devices.join(LIST_SEPARATOR)}`
      : `${devices.join(LIST_SEPARATOR)}${DEVICE_PREFIX_SEPARATOR}${item.baseText}`;
  }
  return rooms.length > 0 ? `${head}\n${ROOMS_LINE_PREFIX}${rooms.join(LIST_SEPARATOR)}` : head;
}

/**
 * Lista „A, B, C" → id znanych pozycji + reszta dosłownie. Dopasowanie zachłanne od najdłuższej
 * nazwy, bo nazwy mogą zawierać przecinek („Piecokuchnia, koza").
 */
function splitList(list: string, candidates: { id: string; text: string }[]): { ids: string[]; other: string[] } {
  const sorted = candidates.filter((c) => c.text.length > 0).sort((a, b) => b.text.length - a.text.length);
  const ids: string[] = [];
  const other: string[] = [];
  let rest = list;
  while (rest) {
    const hit = sorted.find((c) => rest === c.text || rest.startsWith(`${c.text}${LIST_SEPARATOR}`));
    if (hit) {
      if (!ids.includes(hit.id)) ids.push(hit.id);
      else other.push(hit.text);
      rest = rest.slice(hit.text.length + LIST_SEPARATOR.length);
      continue;
    }
    const comma = rest.indexOf(LIST_SEPARATOR);
    other.push(comma < 0 ? rest : rest.slice(0, comma));
    rest = comma < 0 ? '' : rest.slice(comma + LIST_SEPARATOR.length);
  }
  return { ids, other };
}

/**
 * Odtwarza pola wyboru z zapisanego bloku pozycji (widok szczegółów startuje od TEGO, co już
 * wybrano — wcześniej startował pusty i zapis podmieniał treść). `exact` = pola odtwarzają blok
 * znak w znak; false ⇒ treść była zmieniana ręcznie albo to starsza wersja tekstu — przed
 * podmianą trzeba zapytać kominiarza. Nazwy spoza list trafiają do „Inne".
 */
export function parseSelections(
  text: string,
  item: RecommendationItem,
  deviceOptions: ChoiceOption[],
  dictionary: RoomChoice[],
): { selection: Selection; exact: boolean } {
  const matched = itemTexts(item)
    .filter((t) => text.includes(t))
    .sort((a, b) => b.length - a.length)[0];
  if (!matched) return { selection: { ...EMPTY_SELECTION }, exact: false };

  const at = text.indexOf(matched);
  const before = text.slice(0, at);
  const after = text.slice(at + matched.length);
  const newline = after.indexOf('\n');
  const headRest = newline < 0 ? after : after.slice(0, newline);
  const tail = newline < 0 ? '' : after.slice(newline + 1);

  let deviceList = '';
  if (item.devicePosition === 'prefix' && before.endsWith(DEVICE_PREFIX_SEPARATOR)) {
    deviceList = before.slice(0, -DEVICE_PREFIX_SEPARATOR.length);
  } else if (item.devicePosition === 'suffix' && headRest.startsWith(' ')) {
    deviceList = headRest.slice(1);
  }
  // Wiersz pomieszczeń zaraz po treści; dopisek w kolejnych wierszach (zmiana ręczna) nie kasuje
  // odtworzenia pól — o podmianie i tak decyduje `exact`.
  const roomLine = tail.startsWith(ROOMS_LINE_PREFIX) ? tail.split('\n')[0] : '';
  const roomList = roomLine.slice(ROOMS_LINE_PREFIX.length);

  const devices = splitList(deviceList, deviceOptions.flatMap((o) => [
    { id: o.id, text: choiceText(o) },
    { id: o.id, text: o.label.trim() },
  ]));
  const rooms = splitList(roomList, dictionary.flatMap((r) => [
    { id: r.id, text: r.label },
    { id: r.id, text: r.id },
  ]));
  const selection: Selection = {
    devices: devices.ids,
    otherDevice: devices.other.join(LIST_SEPARATOR),
    rooms: rooms.ids,
    otherRoom: rooms.other.join(LIST_SEPARATOR),
  };
  return { selection, exact: buildText(item, selection, deviceOptions, dictionary) === text };
}

// ── „Szczelne Mieszkanie" (builtinKind 'airflow') — podformularz dopływu powietrza ─────────────

export const AIRFLOW_CLOSED_TEXT = 'Urządzenie dopływu jest zamknięte.';

export interface AirflowDetails {
  closed: boolean;
  count: string;
}

export function buildAirflowText(item: RecommendationItem, details: AirflowDetails): string {
  const parts = [item.baseText];
  if (details.closed) parts.push(AIRFLOW_CLOSED_TEXT);
  const count = details.count.trim();
  if (count) parts.push(`Ilość: ${count}.`);
  return parts.join(' ');
}

/** Odtwarza stan podformularza z zapisanego zdania. */
export function parseAirflowDetails(chunk: string): AirflowDetails {
  const count = chunk.match(/Ilość:\s*([\s\S]+?)\.\s*$/);
  return { closed: chunk.includes(AIRFLOW_CLOSED_TEXT), count: count ? count[1].trim() : '' };
}

/** Czy podformularz odtwarza zapisany blok znak w znak (false = dopiski ręczne / starsza treść —
 *  zmiana pola podformularza zastąpiłaby blok, więc trzeba zapytać). */
export const airflowRebuilds = (item: RecommendationItem, text: string): boolean =>
  buildAirflowText(item, parseAirflowDetails(text)) === text;

// ── Cały tekst zaleceń lokalu (bloki rozdzielone pustą linią) ────────────────────────────────

export interface SavedEntry {
  id: string;
  fullText: string;
}

export interface ParsedRecommendations {
  entries: SavedEntry[];
  /** Bloki nierozpoznane (i drugi blok tej samej pozycji) — pole „Inne uwagi". */
  customText: string;
  /** Pierwszy blok tekstu był „Inne uwagi" — zapis odtwarza tę kolejność. */
  customFirst: boolean;
}

export function parseRecommendationText(text: string, items: RecommendationItem[]): ParsedRecommendations {
  const entries: SavedEntry[] = [];
  const leftovers: string[] = [];
  let customFirst = false;
  (text || '').split(/\n{2,}/).forEach((raw) => {
    const chunk = raw.trim();
    if (!chunk) return;
    const item = findItemForChunk(chunk, items);
    if (!item || entries.some((e) => e.id === item.id)) {
      if (entries.length === 0 && leftovers.length === 0) customFirst = true;
      leftovers.push(chunk);
      return;
    }
    entries.push({ id: item.id, fullText: chunk });
  });
  return { entries, customText: leftovers.join('\n\n'), customFirst };
}

/** Odwrotność parseRecommendationText: pozycje w kolejności + „Inne uwagi" na początku albo na końcu. */
export function serializeRecommendations(entries: SavedEntry[], customText: string, customFirst: boolean): string {
  const custom = customText.trim();
  const blocks = entries.map((e) => e.fullText);
  if (custom) {
    if (customFirst) blocks.unshift(custom);
    else blocks.push(custom);
  }
  return blocks.filter((t) => t.length > 0).join('\n\n');
}
